import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNode } from '../contexts/NodeContext';
import { useAuth } from '../contexts/AuthContext';
import { getRecordingSegments } from '../services/api.service';
import { siteCameraKey } from '../api/multiSiteHexAlerts.adapter';
import HexLivePlayer, { HexStreamProvider } from '../components/hexagon/HexLivePlayer';
import type { AlertPlayback, RecordingSegment } from '@trinetra/shared';

/**
 * A site camera's recorded footage and live view, as the alert and event records take them
 * (`playback` for "Clip ±10s", `renderLive` for "Go live"). Null where the user may not see
 * that kind of media, or there is no camera or site.
 *
 * The record's OWN site's recordings, through its tunnel — not the selected site's, which is
 * what the wall-playback adapter would give (and on the all-sites board there is none).
 */
export function useCameraMedia(nodeId?: string | null, cameraId?: string | null, cameraName?: string) {
  const navigate = useNavigate();
  const { selectedNode } = useNode();
  const { hasPermission } = useAuth();
  const canPlayback = hasPermission('recordings.view');
  const canLive = hasPermission('cameras.view');
  const isSelectedSite = !!selectedNode && selectedNode.nodeId === nodeId;

  const playback = useMemo<AlertPlayback | null>(() => {
    if (!canPlayback || !cameraId || !nodeId) return null;
    return {
      url: (from, to) => {
        const token = localStorage.getItem('cloud_token');
        if (!token) return null;
        const qs = new URLSearchParams({ start_time: from, end_time: to, token }).toString();
        return `${window.location.origin}/api/nodes/${nodeId}/recordings/playlist/${cameraId}?${qs}`;
      },
      segments: async (from, to) => {
        const res: any = await getRecordingSegments(nodeId, cameraId, from, to);
        const rows = Array.isArray(res) ? res : Array.isArray(res?.segments) ? res.segments : Array.isArray(res?.data) ? res.data : [];
        return rows as RecordingSegment[];
      },
      tunnelled: true,
      authToken: () => localStorage.getItem('cloud_token'),
      // The Recordings page reads the SELECTED site, so it is offered only when that is this one.
      openFull: isSelectedSite
        ? (from) => {
            const d = new Date(from);
            const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
            navigate(`/recordings?${new URLSearchParams({ camera: cameraId, date }).toString()}`);
          }
        : undefined,
    };
  }, [canPlayback, cameraId, nodeId, isSelectedSite, navigate]);

  // Live over the app's shared node stream. The camera key is site-qualified so the player
  // opens on the record's own node whichever site (if any) is selected.
  const renderLive = useMemo(
    () => (canLive && cameraId && nodeId
      ? () => (
          <HexStreamProvider>
            <HexLivePlayer cameraId={siteCameraKey(nodeId, cameraId)} label={cameraName} className="h-full w-full" />
          </HexStreamProvider>
        )
      : null),
    [canLive, cameraId, nodeId, cameraName],
  );

  return { playback, renderLive };
}
